import { useState, type ReactNode } from "react";
import { Check } from "lucide-react";
import type { RepeatRule } from "@desk/contracts";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Segmented, Select } from "@/components/ui/misc";
import { REPEAT_PRESETS, REPEAT_UNITS, repeatLabel, sameRule } from "@/lib/repeat";

/** Repeat presets plus a Custom dialog (every N units, from the schedule or from completion). */
export function RepeatPicker({
  value,
  onChange,
  children,
}: {
  value: RepeatRule | null;
  onChange: (rule: RepeatRule | null) => void;
  children: ReactNode;
}) {
  const [custom, setCustom] = useState(false);
  const isPreset = !value || REPEAT_PRESETS.some((p) => sameRule(p, value));
  const item = (rule: RepeatRule | null, label: string) => (
    <DropdownMenuItem key={label} onSelect={() => !sameRule(rule, value) && onChange(rule)}>
      <Check className={sameRule(rule, value) ? "opacity-100" : "opacity-0"} />
      {label}
    </DropdownMenuItem>
  );
  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
        <DropdownMenuContent onKeyDown={(e) => e.stopPropagation()}>
          {item(null, "Never")}
          <DropdownMenuSeparator />
          {REPEAT_PRESETS.map((p) => item(p, repeatLabel(p)))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setCustom(true)}>
            <Check className={isPreset ? "opacity-0" : "opacity-100"} />
            {isPreset ? "Custom…" : `${repeatLabel(value!)}…`}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {custom && <CustomRepeatDialog initial={value} onClose={() => setCustom(false)} onConfirm={onChange} />}
    </>
  );
}

function CustomRepeatDialog({
  initial,
  onClose,
  onConfirm,
}: {
  initial: RepeatRule | null;
  onClose: () => void;
  onConfirm: (rule: RepeatRule) => void;
}) {
  const [count, setCount] = useState(String(initial?.interval ?? 1));
  const [unit, setUnit] = useState(initial && initial.frequency !== "weekdays" ? initial.frequency : "weekly");
  const [from, setFrom] = useState<RepeatRule["from"]>(initial?.from ?? "schedule");
  const n = Number(count);
  const valid = Number.isInteger(n) && n >= 1 && n <= 99;
  const rule: RepeatRule = { frequency: unit, interval: valid ? n : 1, from };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[340px]" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <DialogBody>
          <DialogTitle>Repeat</DialogTitle>
          <div className="flex items-center gap-2">
            <label htmlFor="repeat-interval" className="text-[13px] text-muted-foreground">
              Every
            </label>
            <Input
              id="repeat-interval"
              type="number"
              min={1}
              max={99}
              value={count}
              onChange={(e) => setCount(e.target.value)}
              className="w-[64px] tabular"
            />
            <Select
              ariaLabel="Unit"
              value={unit}
              onValueChange={(v) => setUnit(v as typeof unit)}
              options={REPEAT_UNITS.map((u) => ({ value: u.value, label: n === 1 ? u.one : u.many }))}
              className="w-[110px]"
            />
          </div>
          <Segmented
            ariaLabel="Repeat from"
            value={from}
            onChange={setFrom}
            options={[
              { value: "schedule", label: "On schedule" },
              { value: "completion", label: "After completion" },
            ]}
          />
          <p className="text-[13px] text-muted-foreground" aria-live="polite">
            {valid ? repeatLabel(rule) : "Enter a number from 1 to 99."}
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="default"
            disabled={!valid}
            onClick={() => {
              onConfirm(rule);
              onClose();
            }}
          >
            Set repeat
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
